import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Gem, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Home,
  Gift,
  Lamp,
  Palette,
  Building,
  Wine,
  Flower2,
  Sun,
  Lightbulb,
  Frame
} from 'lucide-react';
import { toast } from 'sonner';

// Glass product types
const PRODUCT_TYPES = [
  { value: 'vaso', label: 'Vaso decorativo', icon: Flower2 },
  { value: 'lampada', label: 'Lampada / Lampadario', icon: Lamp },
  { value: 'scultura', label: 'Scultura artistica', icon: Gem },
  { value: 'vetrata', label: 'Vetrata artistica', icon: Frame },
  { value: 'specchio', label: 'Specchio decorato', icon: Sun },
  { value: 'bicchieri', label: 'Bicchieri / Calici', icon: Wine },
  { value: 'centrotavola', label: 'Centrotavola', icon: Flower2 },
  { value: 'gioiello', label: 'Gioiello in vetro', icon: Gem },
  { value: 'fermacarte', label: 'Fermacarte / Soprammobile', icon: Gem },
  { value: 'targa', label: 'Targa / Insegna', icon: Building },
  { value: 'pannello', label: 'Pannello decorativo', icon: Frame },
  { value: 'bomboniera', label: 'Bomboniere', icon: Gift },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Glass techniques
const TECHNIQUES = [
  { value: 'soffiato', label: 'Vetro soffiato a bocca', icon: Gem },
  { value: 'murano', label: 'Tecnica Murano', icon: Star },
  { value: 'fusione', label: 'Fusione (fusing)', icon: Lightbulb },
  { value: 'vetrata', label: 'Vetrata piombata', icon: Frame },
  { value: 'tiffany', label: 'Tecnica Tiffany', icon: Lamp },
  { value: 'incisione', label: 'Incisione / Molatura', icon: Gem },
  { value: 'lavorazione_freddo', label: 'Lavorazione a freddo', icon: Gem },
  { value: 'lampwork', label: 'Lampwork (perle)', icon: Gem },
  { value: 'sabbiatura', label: 'Sabbiatura', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Glass styles
const GLASS_STYLES = [
  { value: 'murano_classico', label: 'Murano classico', icon: Star },
  { value: 'murano_moderno', label: 'Murano contemporaneo', icon: Sparkles },
  { value: 'liberty', label: 'Liberty / Art Nouveau', icon: Flower2 },
  { value: 'art_deco', label: 'Art Déco', icon: Gem },
  { value: 'moderno', label: 'Moderno / Minimalista', icon: Lightbulb },
  { value: 'colorato', label: 'Multicolore / Vivace', icon: Palette },
  { value: 'trasparente', label: 'Trasparente / Cristallo', icon: Gem },
  { value: 'opalino', label: 'Opalino / Lattimo', icon: Sun },
  { value: 'personalizzato', label: 'Personalizzato', icon: Sparkles },
];

// Destinations
const DESTINATIONS = [
  { value: 'casa_soggiorno', label: 'Casa - Soggiorno', icon: Home },
  { value: 'casa_ingresso', label: 'Casa - Ingresso', icon: Home },
  { value: 'illuminazione', label: 'Illuminazione ambiente', icon: Lamp },
  { value: 'ristorante', label: 'Ristorante / Hotel', icon: Building },
  { value: 'chiesa', label: 'Chiesa / Luogo sacro', icon: Building },
  { value: 'ufficio', label: 'Ufficio / Studio', icon: Building },
  { value: 'regalo', label: 'Regalo', icon: Gift },
  { value: 'collezione', label: 'Collezione', icon: Star },
  { value: 'evento', label: 'Evento / Cerimonia', icon: Gift },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'piccolo', label: 'Piccolo (fino a 15cm)', description: 'Soprammobili, gioielli' },
  { value: 'medio', label: 'Medio (15-40cm)', description: 'Vasi, lampade da tavolo' },
  { value: 'grande', label: 'Grande (40-80cm)', description: 'Sculture, lampadari piccoli' },
  { value: 'molto_grande', label: 'Molto grande (oltre 80cm)', description: 'Lampadari, vetrate' },
  { value: 'su_misura', label: 'Su misura specifica', description: 'Dimensioni personalizzate' },
  { value: 'non_so', label: 'Non so ancora', description: 'Vorrei consiglio' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '50-150', label: '€50 - €150', description: 'Piccoli oggetti' },
  { value: '150-400', label: '€150 - €400', description: 'Oggetti medi decorati' },
  { value: '400-800', label: '€400 - €800', description: 'Pezzi pregiati' },
  { value: '800-1500', label: '€800 - €1.500', description: 'Opere elaborate' },
  { value: '1500-3000', label: '€1.500 - €3.000', description: 'Lampadari, sculture' },
  { value: '3000+', label: '€3.000+', description: 'Vetrate, installazioni' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Piccoli oggetti' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Opere complesse' },
  { value: '3months', label: '3+ Mesi', description: 'Grandi installazioni' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example products
const GLASS_EXAMPLES = [
  {
    idea: "Lampadario per sala da pranzo stile Murano",
    result: "Lampadario a 8 bracci in vetro soffiato multicolore, fiori e foglie in cristallo, struttura dorata",
    image: "https://images.unsplash.com/photo-1674348016481-9e93a9e2471b?w=400&h=300&fit=crop",
    category: "Lampadari"
  },
  {
    idea: "Vetrata artistica per porta d'ingresso",
    result: "Pannello 80x200cm in vetro piombato con disegno floreale Art Nouveau, vetri opalini e trasparenti",
    image: "https://images.unsplash.com/photo-1584476037416-2c251d2b0bda?w=400&h=300&fit=crop",
    category: "Vetrate"
  },
  {
    idea: "Set di calici personalizzati per matrimonio",
    result: "6 calici in cristallo soffiato con iniziali degli sposi incise e dettagli in oro 24k",
    image: "https://images.unsplash.com/photo-1772992552245-eb3ab1539c6f?w=400&h=300&fit=crop",
    category: "Bicchieri"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Il lampadario in vetro di Murano che ho commissionato è diventato il protagonista del mio salone. Un pezzo unico che cattura la luce in modo magico!",
    author: "Elisabetta V.",
    location: "Venezia"
  },
  {
    text: "Ho fatto realizzare una vetrata per la mia villa. L'artigiano ha interpretato il mio disegno alla perfezione. Ogni volta che la luce la attraversa è uno spettacolo.",
    author: "Giovanni M.",
    location: "Como"
  },
  {
    text: "I calici personalizzati per il nostro anniversario sono meravigliosi. Dettagli incredibili, qualità eccezionale. Li useremo per sempre.",
    author: "Laura e Marco",
    location: "Firenze"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Che tipo di oggetti in vetro posso richiedere?",
    answer: "Puoi richiedere qualsiasi creazione in vetro artistico: vasi, lampadari, sculture, vetrate artistiche, specchi decorati, bicchieri e calici, centrotavola, gioielli in vetro, fermacarte, targhe, pannelli decorativi, bomboniere e molto altro."
  },
  {
    question: "Quali tecniche di lavorazione sono disponibili?",
    answer: "I nostri maestri vetrai padroneggiano tutte le tecniche tradizionali e moderne: vetro soffiato a bocca, tecnica Murano, fusione (fusing), vetrata piombata, tecnica Tiffany, incisione e molatura, lampwork per perle e gioielli, sabbiatura decorativa."
  },
  {
    question: "Il vetro di Murano è davvero speciale?",
    answer: "Sì, il vetro di Murano è rinomato nel mondo per la sua qualità eccezionale, i colori vibranti e le tecniche segrete tramandate da generazioni di maestri vetrai. Ogni pezzo è un'opera d'arte unica, soffiata a mano secondo tradizioni secolari."
  },
  {
    question: "Posso personalizzare completamente il design?",
    answer: "Assolutamente! Puoi richiedere colori specifici, forme personalizzate, incisioni con nomi, date o disegni, combinazioni di tecniche diverse. Gli artigiani possono anche riprodurre un design da una foto o un disegno."
  },
  {
    question: "Quanto tempo ci vuole per realizzare un oggetto in vetro?",
    answer: "I tempi variano in base alla complessità: un piccolo oggetto soffiato richiede 1-2 settimane, un lampadario medio 4-6 settimane, una vetrata artistica grande 2-3 mesi. L'artigiano fornirà una stima precisa con la proposta."
  }
];

export default function VetroRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    product_type: '',
    technique: '',
    style: '',
    destination: '',
    size: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Vetro Artistico su Misura | Maestri Vetrai Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `glass-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi l\'oggetto in vetro che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_glass_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const productLabel = PRODUCT_TYPES.find(t => t.value === formData.product_type)?.label || '';
      const techniqueLabel = TECHNIQUES.find(t => t.value === formData.technique)?.label || '';
      const styleLabel = GLASS_STYLES.find(s => s.value === formData.style)?.label || '';
      const destinationLabel = DESTINATIONS.find(d => d.value === formData.destination)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(s => s.value === formData.size)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (productLabel) details.push(`🔮 Tipo: ${productLabel}`);
      if (techniqueLabel) details.push(`🔥 Tecnica: ${techniqueLabel}`);
      if (styleLabel) details.push(`🎨 Stile: ${styleLabel}`);
      if (destinationLabel) details.push(`🏠 Destinazione: ${destinationLabel}`);
      if (sizeLabel) details.push(`📐 Dimensioni: ${sizeLabel}`);
      if (budgetLabel) details.push(`💰 Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️ Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${productLabel} in vetro su misura`.trim() || 'Vetro artistico su misura',
        description: fullDescription,
        category: 'Lavorazione Vetro',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai maestri vetrai.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('glass-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Vetro Artistico su Misura | Murano, Lampadari, Vetrate | Rarifex</title>
        <meta name="description" content="Commissiona vetro artistico su misura da maestri vetrai italiani. Vetro di Murano, lampadari, vetrate artistiche, sculture in vetro soffiato. Tecniche tradizionali, pezzi unici. Gratuito e senza impegno." />
        <meta name="keywords" content="vetro artistico su misura, vetro Murano, lampadario artigianale, vetrata artistica, vetro soffiato, maestro vetraio, Made in Italy, scultura vetro" />
        <link rel="canonical" href="https://www.rarifex.com/vetro-su-misura" />
        <meta property="og:title" content="Vetro Artistico su Misura | Maestri Vetrai Italiani | Rarifex" />
        <meta property="og:description" content="Commissiona vetro artistico da maestri vetrai italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/vetro-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-cyan-50/50 via-white to-teal-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Gem className="w-4 h-4" />
                    Vetro Artistico
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Vetro italiano{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-teal-600">
                      soffiato per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Lampadari, vetrate, sculture e oggetti unici.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Maestri vetrai italiani creano capolavori con tecniche secolari: vetro soffiato a bocca, tecnica Murano, vetrate artistiche.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Soffiato a mano</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-cyan-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>Tradizione Murano</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <Card id="glass-form" className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-slate-800">Descrivi la tua creazione</h2>
                      <p className="text-slate-500 text-sm mt-1">Ricevi proposte da maestri vetrai</p>
                    </div>
                    
                    {/* Title */}
                    <div>
                      <Label className="text-slate-700">Titolo progetto (opzionale)</Label>
                      <Input
                        placeholder="Es: Lampadario per salone, Vetrata per ingresso..."
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Description */}
                    <div>
                      <Label className="text-slate-700">Descrivi cosa desideri *</Label>
                      <Textarea
                        placeholder="Descrivi l'oggetto in vetro che vorresti: tipo, dimensioni, colori, stile, dove verrà posizionato..."
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="mt-1 min-h-[100px]"
                        required
                      />
                    </div>
                    
                    {/* Product Type & Technique Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Tipo di oggetto</Label>
                        <select
                          value={formData.product_type}
                          onChange={(e) => handleInputChange('product_type', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Seleziona...</option>
                          {PRODUCT_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Tecnica</Label>
                        <select
                          value={formData.technique}
                          onChange={(e) => handleInputChange('technique', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Seleziona...</option>
                          {TECHNIQUES.map(tech => (
                            <option key={tech.value} value={tech.value}>{tech.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Style */}
                    <div>
                      <Label className="text-slate-700">Stile</Label>
                      <select
                        value={formData.style}
                        onChange={(e) => handleInputChange('style', e.target.value)}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="">Seleziona...</option>
                        {GLASS_STYLES.map(style => (
                          <option key={style.value} value={style.value}>{style.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Destination & Size Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Destinazione</Label>
                        <select
                          value={formData.destination}
                          onChange={(e) => handleInputChange('destination', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Seleziona...</option>
                          {DESTINATIONS.map(dest => (
                            <option key={dest.value} value={dest.value}>{dest.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Dimensioni</Label>
                        <select
                          value={formData.size}
                          onChange={(e) => handleInputChange('size', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Seleziona...</option>
                          {SIZE_OPTIONS.map(size => (
                            <option key={size.value} value={size.value}>{size.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Budget & Timeline Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Budget</Label>
                        <select
                          value={formData.budget}
                          onChange={(e) => handleInputChange('budget', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Seleziona...</option>
                          {BUDGET_OPTIONS.map(budget => (
                            <option key={budget.value} value={budget.value}>{budget.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Tempistiche</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(timeline => (
                            <option key={timeline.value} value={timeline.value}>{timeline.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Image Upload */}
                    <div>
                      <Label className="text-slate-700">Immagini di riferimento (opzionale)</Label>
                      <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-cyan-300 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                          id="glass-images"
                          disabled={uploadingFile}
                        />
                        <label htmlFor="glass-images" className="cursor-pointer flex flex-col items-center gap-2">
                          {uploadingFile ? (
                            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
                          ) : (
                            <Upload className="w-8 h-8 text-slate-400" />
                          )}
                          <span className="text-sm text-slate-500">
                            {uploadingFile ? 'Caricamento...' : 'Clicca per caricare (max 5 immagini)'}
                          </span>
                        </label>
                      </div>
                      
                      {/* Uploaded images preview */}
                      {formData.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.attachments.map((url, index) => (
                            <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden group">
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              >
                                <X className="w-5 h-5 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-5 h-5 mr-2" />
                      )}
                      {loading ? 'Invio in corso...' : 'Ricevi proposte gratis'}
                    </Button>
                    
                    <p className="text-xs text-slate-400 text-center">
                      Gratuito e senza impegno. Riceverai proposte entro 24-48 ore.
                    </p>
                  </form>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come funziona
              </h2>
              <p className="text-lg text-slate-600">
                Dalla tua idea al capolavoro in vetro in 4 semplici passi
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-8">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Racconta l\'oggetto che desideri: tipo, stile, colori, dimensioni' },
                { icon: Users, title: 'Ricevi', desc: 'I maestri vetrai interessati ti inviano le loro proposte' },
                { icon: CheckCircle, title: 'Scegli', desc: 'Valuta qualità, portfolio e preventivi per scegliere il migliore' },
                { icon: Heart, title: 'Ammira', desc: 'Segui la creazione e ricevi il tuo pezzo unico' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white shadow-lg">
                    <step.icon className="w-8 h-8" />
                  </div>
                  <div className="text-sm font-semibold text-cyan-600 mb-1">Passo {index + 1}</div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Esempi di creazioni realizzate
              </h2>
              <p className="text-lg text-slate-600">
                Dall'idea al capolavoro in vetro
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {GLASS_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-[4/3] relative">
                    <img src={example.image} alt={example.category} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-cyan-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {example.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="mb-3">
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">L'idea</div>
                      <p className="text-slate-700 font-medium">{example.idea}</p>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Il risultato</div>
                      <p className="text-slate-600 text-sm">{example.result}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa dicono i nostri clienti
              </h2>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6 bg-gradient-to-br from-cyan-50 to-teal-50 border-0">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-cyan-200 flex items-center justify-center text-cyan-700 font-bold">
                      {testimonial.author[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">{testimonial.author}</div>
                      <div className="text-sm text-slate-500">{testimonial.location}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Domande frequenti
                </h2>
              </div>
              
              <div className="space-y-3">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index} 
                    className={`overflow-hidden transition-all cursor-pointer ${expandedFaq === index ? 'ring-2 ring-cyan-200' : ''}`}
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-4 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-800 pr-4">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-4 pb-4 text-slate-600 border-t border-slate-100 pt-3">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-cyan-600 to-teal-600">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <Gem className="w-16 h-16 mx-auto mb-6 text-white/90" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Pronto a creare il tuo capolavoro in vetro?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Descrivi la tua idea e ricevi proposte da maestri vetrai italiani. Gratuito e senza impegno.
              </p>
              <Button 
                size="lg" 
                className="bg-white text-cyan-600 hover:bg-cyan-50 font-semibold text-lg px-8 py-6 h-auto shadow-xl"
                onClick={scrollToForm}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Inizia ora
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-800 mb-2">Accedi per inviare la richiesta</h3>
            <p className="text-slate-600 mb-6">
              Per ricevere proposte dai maestri vetrai devi accedere o creare un account gratuito.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAuthPrompt(false)}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                onClick={() => navigate('/login?redirect=/vetro-su-misura')}
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
